import { ReactNode, useState, useEffect, useId } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import Tree, { TreeNode, fileIconForName } from "./Tree/Tree";
import { buildStorageTree } from "./Tree/storageTree";
import { useStorageTree } from "./Tree/useStorageTree";
import { formatFileSize } from "../../utilities/fileFacts";
import { KnowledgeBase } from "@the-real-insight/tri-model";
import { storageAPI } from "../../StorageAPI";
import { ManagedFolderListResult, ManagedFileProcessingState } from "@the-real-insight/tri-model";
import { setManagedFiles, getKnowledgeBasesForFile } from "../../KnowledgeBasesAPI";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { useModalContext } from "../../components/ModalProvider";
import MarkdownEditor from "../../components/MarkdownEditor";

import { Check, Clock, Eye, FileText, HardDrive, Loader2, Pencil, UploadCloud, X } from "lucide-react";

import { EASE_OUT } from "@/lib/ease";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PLATFORM_VOLUME_KEY } from "../../StorageAPI";
import { isViewableDocument, useDocumentViewer } from "@/documentViewer/DocumentViewerContext";

/**
 * The file manager always works on the internal S3 volume — it is the write surface (upload, move,
 * rename, delete, edit), and writes belong in the one volume every customer always has.
 *
 * Deliberately the explicit platform key, **not** the `default` alias: a customer's default volume
 * can be a SharePoint one (that setting governs where *generated* files land), and `default` would
 * quietly turn this into a write surface for their SharePoint site. The volume switcher lives only
 * in the read-only pickers, where browsing another volume is the point.
 */
const STORAGE_VOLUME = PLATFORM_VOLUME_KEY;

/** Dateien und Ordner unter einem Ordner, über alle Ebenen — für den Löschdialog. */
export const countFolderContents = (files: TreeNode[] = []): { fileCount: number; folderCount: number } => {
    let fileCount = 0;
    let folderCount = 0;
    for (const node of files) {
        if (node.type === 'folder') {
            folderCount += 1;
            const nested = countFolderContents(node.files);
            fileCount += nested.fileCount;
            folderCount += nested.folderCount;
        } else {
            fileCount += 1;
        }
    }
    return { fileCount, folderCount };
};

/**
 * Ein Abschnitt des Detailbereichs, der nur zu manchen Dateien gehört. Er klappt beim Erscheinen
 * auf und beim Verschwinden zu — beim ersten Aufbau des Bereichs steht er ohne Bewegung da, damit
 * ein Dateiwechsel nur zeigt, was sich wirklich ändert. Der Außenabstand gehört in den Inhalt
 * (nicht hierher), weil der abgeschnittene Rahmen ihn sonst mit zuklappen müsste.
 */
function Reveal({ show, children }: { show: boolean; children: ReactNode }) {
    const reduceMotion = useReducedMotion();
    return (
        <AnimatePresence initial={false}>
            {show ? (
                <motion.div
                    key="reveal"
                    initial={reduceMotion ? false : { height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0.1 : 0.25, ease: EASE_OUT }}
                    className="overflow-hidden"
                >
                    {children}
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

/** Breite des Detailbereichs in px — Motion animiert Zahlen, keine `min()`-Ausdrücke. */
const DETAILS_WIDTH = 400;

interface FileManagementProps {
    selectedKnowledgeBase?: any;
    updateSelectedKnowledgeBase?: (knowledgeBase: any) => void;
    authentication: any;
    pushError?: (error: any) => void;
    showDeleteDialog?: (title: string, name: string, onConfirm: () => Promise<void>) => void;
}

export default function FileManagement({
    selectedKnowledgeBase,
    updateSelectedKnowledgeBase,
    authentication,
    pushError = () => {},
    showDeleteDialog = () => {}
}: FileManagementProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const documentViewer = useDocumentViewer();
    const sharedFieldId = useId();
    const linkFieldId = useId();
    const sourceFieldId = useId();
    const reduceMotion = useReducedMotion();
    // File management state
    const [selectedFile, setSelectedFile] = useState<any>();
    const [selectedFileKnowledgeBases, setSelectedFileKnowledgeBases] = useState<any>([]);

    // Upload state management
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<{ files: number; current: number } | null>(null);
    
    // Markdown editor modal state
    const [isMarkdownEditorOpen, setIsMarkdownEditorOpen] = useState<boolean>(false);
    const [markdownContent, setMarkdownContent] = useState<string>('');
    const [originalMarkdownContent, setOriginalMarkdownContent] = useState<string>('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);
    const [isSavingContent, setIsSavingContent] = useState<boolean>(false);

    const { showConfirmationDialog } = useModalContext() as any;

    // PDF preview in the details pane: fetched as blob, shown via object URL.
    const [pdfUrl, setPdfUrl] = useState<string | undefined>();
    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
    const [pdfFailed, setPdfFailed] = useState<boolean>(false);

    const isPdfSelected = !!selectedFile?.path?.toLowerCase().endsWith('.pdf');

    useEffect(() => {
        if (!isPdfSelected) {
            setPdfUrl(undefined);
            setPdfFailed(false);
            setIsPdfLoading(false);
            return;
        }

        let cancelled = false;
        let createdUrl: string | undefined;
        setIsPdfLoading(true);
        setPdfUrl(undefined);
        setPdfFailed(false);

        (async () => {
            try {
                const { blob } = await storageAPI.downloadFile(authentication.customer, STORAGE_VOLUME, selectedFile.path);
                const objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                createdUrl = objectUrl;
                if (!cancelled) setPdfUrl(objectUrl);
            } catch (error) {
                console.error('Error loading PDF preview:', error);
                if (!cancelled) setPdfFailed(true);
            } finally {
                if (!cancelled) setIsPdfLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (createdUrl) {
                try {
                    URL.revokeObjectURL(createdUrl);
                } catch {
                    /* noop */
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPdfSelected, selectedFile?.path]);
    
    const handleClick = (_props: any) => {
        // do extra stuff with this data
        // {state, id, name, type, path?, parent?, level? }
        // console.log(props);
    };

    const handleUpdate = (state: any) => {
        localStorage.setItem(
            "tree",
            JSON.stringify(state, function (key, value) {
                if (key === "parent" || key === "id") {
                    return null;
                }
                return value;
            })
        );
    };


    /** Eine PDF im Dokumentbetrachter öffnen — derselbe wie für Quellen im Chat, über die Datei-Id. */
    const viewFile = (item: any) =>
        documentViewer?.openDocument({ fileId: String(item._id), filename: item.name || item.path?.split('/').pop() });
    const canViewFile = (item: any) => !!documentViewer && !!item?._id && isViewableDocument(item.name || item.path, item.mimeType);

    // Create a hierarchical structure
    const createFileNode = (item: any, knowledgeBase?: any): TreeNode => {
        const fileWithMetadata = {
            ...item,
            link: item.link || '',
            source: item.source || '',
            shared: item.shared || false
        };

        // Keeping knowledgeBaseFiles for backwards compability
        let knowledgeBaseFiles: any = {};
        if (knowledgeBase?.files) {
            knowledgeBaseFiles = knowledgeBase.files.reduce((acc: any, file: any) => {
                acc[file.path] = file;
                return acc;
            }, {});
        }

        let knowledgeBaseManagedFiles: any = {};
        if (knowledgeBase?.managedFiles) {
            knowledgeBaseManagedFiles = knowledgeBase.managedFiles.reduce((acc: any, file: any) => {
                acc[file.path] = file;
                return acc;
            }, {}) 
        }

        knowledgeBaseFiles = { ...knowledgeBaseFiles, ...knowledgeBaseManagedFiles };
        

        return {
            type: 'file' as const,
            name: item.name,
            path: item.path,
            selected: !!knowledgeBaseFiles[item.path],
            processing: (item.processingState === ManagedFileProcessingState.processing || item.processingState === ManagedFileProcessingState.queued),
            showCheckbox: !!selectedKnowledgeBase,
            processingState: item.processingState,
            onSelect: (checked: boolean) => {
                let newKnowledgeBase = new KnowledgeBase({ ...selectedKnowledgeBase });
                item.selected = checked;

                if (checked) {
                    newKnowledgeBase.addFile(item);
                } else {
                    newKnowledgeBase.removeFile(item);
                }

                console.log('newKnowledgeBase', newKnowledgeBase);
                if (newKnowledgeBase._id && Array.isArray(newKnowledgeBase.managedFiles)) {
                    const managedFiles = newKnowledgeBase.managedFiles.map(file => file._id);
                    setManagedFiles(authentication.customer, newKnowledgeBase._id, managedFiles);
                    updateSelectedKnowledgeBase?.(newKnowledgeBase);
                    selectedKnowledgeBase = newKnowledgeBase;
                } else {
                    console.error('newKnowledgeBase._id is undefined or managedFiles is not an array');
                }
                
                // Update the local tree state to reflect the checkbox change
                setData(prevData => {
                    const updateSelectedState = (nodes: TreeNode[]): TreeNode[] => {
                        return nodes.map(node => {
                            if (node.path === item.path) {
                                return { ...node, selected: checked };
                            }
                            if (node.files) {
                                return { ...node, files: updateSelectedState(node.files) };
                            }
                            return node;
                        });
                    };
                    return updateSelectedState(prevData);
                });
            },
            onClick: () => {
                const _knowledgeBases = getKnowledgeBasesForFile(authentication.customer, item._id).then(knowledgeBases => {
                    setSelectedFileKnowledgeBases(knowledgeBases.knowledgeBases);
                });
                // Ein zweiter Klick auf die gewählte Datei wählt sie ab. Der Abschluss ist so alt wie
                // die letzte Auflistung, deshalb entscheidet der aktuelle Stand, nicht `selectedFile`.
                setSelectedFile((current: any) => (current?.path === fileWithMetadata.path ? undefined : fileWithMetadata));
            },
            ...(canViewFile(item) ? { onView: () => viewFile(item) } : {}),
            onDelete: async (deleteCallback: () => void) => {
                showDeleteDialog(`Datei ${item.name} löschen`,
                    item.name,
                    async () => {
                        console.log('File deleted', item.path);
                        await storageAPI.deleteFile(authentication.customer, STORAGE_VOLUME, item.path);
                        deleteCallback();
                    });
            },
            onRename: async (name: string) => {
                const rootPath = item.path.split('/').slice(0, -1).join('/');
                console.log(`Rename file ${item.path}`, `${rootPath}/${name}`);
                await storageAPI.renameFile(authentication.customer, STORAGE_VOLUME, item.path, name);
                console.log('File renamed successfully');
                // Note: updateStorage() removed - local state is updated via dispatch in TreeFile component
            },
            onDownload: async () => {
                console.log(`Download file ${item.path}`);

                try {
                    const { blob, filename } = await storageAPI.downloadFile(authentication.customer, STORAGE_VOLUME, item.path);

                    const url = URL.createObjectURL(blob);

                    // Create a temporary link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;

                    // Append to body, click, and remove
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Clean up the URL object after a delay
                    setTimeout(() => URL.revokeObjectURL(url), 100);
                } catch (error) {
                    console.error('Error downloading file:', error);
                    pushError('Fehler beim Herunterladen der Datei');
                }
            }
        };
    };

    const createFolderNode = (path: string, name: string, files: TreeNode[] = []): TreeNode => {
        return {
            type: 'folder',
            name: name,
            path: path,
            isOpen: folderStates.get(path) ?? (path === '/'), // Use preserved state or default
            files: files,
            isUploading: false, // Will be set dynamically during upload
            onSelect: (checked: boolean) => {
                console.log('Folder checked', path, checked);
            },
            onToggleOpen: (isOpen: boolean) => {
                console.log('Folder toggle:', path, 'isOpen:', isOpen);
                updateFolderState(path, isOpen);
            },
            onDelete: async (deleteCallback: () => void) => {
                /**
                 * Ein Ordner geht samt Inhalt — auch samt Unterordnern. Der Dialog nennt, wie viel
                 * das ist, damit niemand „Ordner löschen" liest und einen leeren meint. Die Zahl
                 * kommt aus dem Baum, der gerade angezeigt wird, nicht aus einer neuen Abfrage.
                 */
                const { fileCount, folderCount } = countFolderContents(files);
                const contents: string[] = [];
                if (fileCount > 0) contents.push(`${fileCount} ${fileCount === 1 ? 'Datei' : 'Dateien'}`);
                if (folderCount > 0) contents.push(`${folderCount} ${folderCount === 1 ? 'Unterordner' : 'Unterordner'}`);
                const title = contents.length
                    ? `Ordner ${name} samt Inhalt löschen (${contents.join(', ')})`
                    : `Ordner ${name} löschen`;

                await showDeleteDialog(
                    title,
                    name,
                    async () => {
                        console.log('Folder deleted', path);
                        //remove tailing slash
                        path = path.replace(/\/$/, '');
                        await storageAPI.deleteObject(authentication.customer, STORAGE_VOLUME, path);
                        deleteCallback();
                    });
            },
            onCreateFolder: async (name: string, createCallback: () => void) => {
                console.log(`Create folder in ${path}`, name);
                const newFolderPath = `${path}${name}`.replace('//', '/');
                await storageAPI.createFolder(authentication.customer, STORAGE_VOLUME, newFolderPath);
                createCallback();
                toast.success(`Ordner „${name}“ erstellt`);
                // Refresh the storage to ensure the new folder gets proper handlers
                updateStorage();
            },
            onCreateFile: async (name: string, createCallback: () => void) => {
                console.log(`Create file in ${path}`, name);
                createCallback();
            },
            onRename: async (name: string) => {
                const pathParts = path.split('/').filter((part: string) => part !== '');
                const rootPath = pathParts.slice(0, -1).join('/');
                console.log(`Rename folder ${path}`, `${rootPath}/${name}`);
                await storageAPI.renameFolder(authentication.customer, STORAGE_VOLUME, path, name);
                console.log('Folder renamed successfully');
                // Note: updateStorage() removed - local state is updated via dispatch in TreeFolder component
            },
            onFileDrop: async (files: File[], folderPath: string) => {
                if (isUploading) {
                    console.log('Upload already in progress, skipping...');
                    return;
                }
                
                console.log(`Uploading ${files.length} files to folder: ${folderPath}`);
                console.log('DEBUG: folderPath type:', typeof folderPath, 'value:', JSON.stringify(folderPath));
                console.log('DEBUG: folderPath length:', folderPath.length);
                setIsUploading(true);
                setUploadProgress({ files: files.length, current: 0 });
                
                // Update the specific folder's upload state by matching the folder path
                setData(prevData => {
                    const updateFolderUploadState = (nodes: TreeNode[]): TreeNode[] => {
                        return nodes.map(node => {
                            if (node.type === 'folder' && node.path === folderPath) {
                                return { ...node, isUploading: true };
                            }
                            if (node.files) {
                                return { ...node, files: updateFolderUploadState(node.files) };
                            }
                            return node;
                        });
                    };
                    return updateFolderUploadState(prevData);
                });
                
                try {
                    // Upload files to the specified folder path
                    await storageAPI.uploadFiles(authentication.customer, STORAGE_VOLUME, files, folderPath, true);
                    console.log('Files uploaded successfully');
                    setUploadProgress({ files: files.length, current: files.length });
                    toast.success(`${files.length} Datei${files.length > 1 ? 'en' : ''} hochgeladen`);
                    
                    // Small delay to show completion
                    setTimeout(() => {
                        setUploadProgress(null);
                    }, 1000);
                    
                    // Refresh the storage to show the new files
                    updateStorage();
                } catch (error) {
                    console.error('Error uploading files:', error);
                    pushError('Fehler beim Hochladen der Dateien');
                    setUploadProgress(null);
                } finally {
                    setIsUploading(false);
                    
                    // Clear the folder's upload state by matching the folder path
                    setData(prevData => {
                        const clearFolderUploadState = (nodes: TreeNode[]): TreeNode[] => {
                            return nodes.map(node => {
                                if (node.type === 'folder' && node.path === folderPath) {
                                    return { ...node, isUploading: false };
                                }
                                if (node.files) {
                                    return { ...node, files: clearFolderUploadState(node.files) };
                                }
                                return node;
                            });
                        };
                        return clearFolderUploadState(prevData);
                    });
                }
            },
            onFileMove: async (fileItem: any, destinationPath: string) => {
                try {
                    console.log(`Moving file ${fileItem.name} from ${fileItem.path} to ${destinationPath}`);
                    
                    // Construct the destination path with the filename
                    const destinationFilePath = `${destinationPath}/${fileItem.name}`.replace('//', '/');
                    
                    // Use the moveFile API to move the file
                    await storageAPI.moveFile(authentication.customer, STORAGE_VOLUME, fileItem.path, destinationFilePath);
                    
                    console.log('File moved successfully');
                    
                    // Refresh the storage to show the updated structure
                    updateStorage();
                } catch (error) {
                    console.error('Error moving file:', error);
                    pushError('Fehler beim Verschieben der Datei');
                }
            }
        };
    };

    /**
     * Die Auflistung des Speichers in den Baum, den der `Tree` zeichnet.
     *
     * Die Verschachtelung selbst macht `buildStorageTree` — sie steckt allein in den Pfaden und ist
     * für den Datenraum dieselbe. Hier bleibt nur, was diese Ansicht ausmacht: dass ein Dateiknoten
     * die Angaben seiner Wissensbasis mitbekommt.
     */
    const toTreeNodes = (folderContents: ManagedFolderListResult, knowledgeBase?: any): TreeNode[] =>
        buildStorageTree(folderContents.items as any, {
            createFile: (item: any) => createFileNode(item, knowledgeBase),
            createFolder: (path: string, name: string, files: TreeNode[]) =>
                createFolderNode(path, name, files),
        });

    /**
     * Auflisten, Klappzustände bewahren, Drosselungen abwarten — alles in `useStorageTree`.
     *
     * Der Haken hält den Baum, weil der Datenraum denselben braucht. Was diese Ansicht ausmacht,
     * bleibt hier: `toTreeNodes` entscheidet, wie eine Zeile aussieht.
     */
    const {
        data,
        setData,
        isInitialLoading,
        folderStates,
        updateFolderState,
        refresh,
    } = useStorageTree({
        customer: authentication?.customer,
        volume: STORAGE_VOLUME,
        buildNodes: (listing) => toTreeNodes(listing, selectedKnowledgeBase),
        onError: pushError,
    });

    /** Neu auflisten. `force` umgeht die Entprellung — das tut nur der Aktualisieren-Knopf. */
    const updateStorage = (force = false) => refresh({ force });

    useEffect(() => {
        updateStorage();
    }, [selectedKnowledgeBase?._id]);

    // Root level file drop handler
    const handleRootFileDrop = async (files: File[], _folderPath: string) => {
        if (isUploading) {
            console.log('Upload already in progress, skipping...');
            return;
        }
        
        console.log(`Uploading ${files.length} files to root folder`);
        setIsUploading(true);
        setUploadProgress({ files: files.length, current: 0 });
        
        try {
            // Upload files to the root (empty path)
            await storageAPI.uploadFiles(authentication.customer, STORAGE_VOLUME, files, '', true);
            console.log('Files uploaded successfully to root');
            setUploadProgress({ files: files.length, current: files.length });
            toast.success(`${files.length} Datei${files.length > 1 ? 'en' : ''} hochgeladen`);
            
            // Small delay to show completion
            setTimeout(() => {
                setUploadProgress(null);
            }, 1000);
            
            // Refresh the storage to show the new files
            updateStorage();
        } catch (error) {
            console.error('Error uploading files to root:', error);
            pushError('Fehler beim Hochladen der Dateien');
            setUploadProgress(null);
        } finally {
            setIsUploading(false);
        }
    };

    // Root level file move handler
    const handleRootFileMove = async (fileItem: any, _destinationPath: string) => {
        try {
            console.log(`Moving file ${fileItem.name} to root folder`);
            
            // Construct the destination path with the filename (root is empty path)
            const destinationFilePath = fileItem.name;
            
            // Use the moveFile API to move the file to root
            await storageAPI.moveFile(authentication.customer, STORAGE_VOLUME, fileItem.path, destinationFilePath);
            
            console.log('File moved to root successfully');
            
            // Refresh the storage to show the updated structure
            updateStorage();
        } catch (error) {
            console.error('Error moving file to root:', error);
            pushError('Fehler beim Verschieben der Datei');
        }
    };

    // Root level folder creation handler
    const handleRootFolderCreate = async (name: string) => {
        try {
            console.log(`Creating root folder: ${name}`);
            
            // Create folder at root level (empty path)
            await storageAPI.createFolder(authentication.customer, STORAGE_VOLUME, name);

            console.log('Root folder created successfully');
            toast.success(`Ordner „${name}“ erstellt`);
            
            // Refresh the storage to show the new folder
            updateStorage();
        } catch (error) {
            console.error('Error creating root folder:', error);
            pushError('Fehler beim Erstellen des Ordners');
        }
    };

    const goToKnowledgeBase = (knowledgeBaseId: string, knowledgeBaseName: string) => {
        showConfirmationDialog(
            t('ui.goToKnowledgeBase') + ' ' + knowledgeBaseName,
            t('ui.goToKnowledgeBaseConfirm', { name: knowledgeBaseName }),
            async () => {
                navigate(`/settings?panel=agent&tab=2&knowledgeBase=${knowledgeBaseId}&key=documents`);
            }
        );
    };

    // Markdown editor handlers
    const handleOpenMarkdownEditor = async () => {
        if (!selectedFile) return;
        
        setIsLoadingContent(true);
        setIsMarkdownEditorOpen(true);
        
        try {
            // Fetch actual file content from server
            const content = await storageAPI.getFileContent(authentication.customer, STORAGE_VOLUME, selectedFile.path);
            setMarkdownContent(content);
            setOriginalMarkdownContent(content);
            setHasUnsavedChanges(false);
        } catch (error: any) {
            console.error('Error loading file content:', error);
            pushError('Failed to load file content: ' + error.message);
            
            // Fallback to empty content
            setMarkdownContent('');
            setOriginalMarkdownContent('');
            setHasUnsavedChanges(false);
        } finally {
            setIsLoadingContent(false);
        }
    };

    const handleMarkdownContentChange = (value: string | undefined) => {
        const newContent = value || '';
        setMarkdownContent(newContent);
        setHasUnsavedChanges(newContent !== originalMarkdownContent);
    };

    const handleSaveMarkdown = async () => {
        if (!selectedFile) return;
        
        setIsSavingContent(true);
        
        try {
            // Save content to server
            await storageAPI.updateFileContent(authentication.customer, STORAGE_VOLUME, selectedFile.path, markdownContent);
            console.log('Markdown content saved successfully');
            
            setOriginalMarkdownContent(markdownContent);
            setHasUnsavedChanges(false);
            setIsMarkdownEditorOpen(false);
            
            // Refresh storage to show updated file metadata
            updateStorage();
        } catch (error: any) {
            console.error('Error saving file content:', error);
            pushError('Failed to save file content: ' + error.message);
        } finally {
            setIsSavingContent(false);
        }
    };

    const handleCancelMarkdown = () => {
        if (hasUnsavedChanges) {
            showConfirmationDialog(
                'Discard Changes',
                'You have unsaved changes. Are you sure you want to discard them?',
                () => {
                    setIsMarkdownEditorOpen(false);
                    setHasUnsavedChanges(false);
                }
            );
        } else {
            setIsMarkdownEditorOpen(false);
        }
    };


    return (
        <>
            {selectedKnowledgeBase && (
                <>
                    <div className="mt-5 text-sm">Wähle Verzeichnisse und/oder Dateien aus deinem Dokumentenbestand aus, die in diesem Wissensangebot verwendet werden sollen.</div>
                    <div className="mt-3 text-sm">Du kannst Dateien per Drag &amp; Drop direkt in Ordner ziehen. Neue Verzeichnisse können auch erstellt werden.</div>
                </>
            )}

            {/* Upload Progress Indicator */}
            <AnimatePresence initial={false}>
                {uploadProgress && (
                    <motion.div
                        key="upload-progress"
                        initial={{ opacity: 0, y: -8, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                        exit={{ opacity: 0, y: -8, height: 0 }}
                        transition={{ duration: 0.25, ease: EASE_OUT }}
                        className="overflow-hidden"
                    >
                        {(() => {
                            const isDone = uploadProgress.current === uploadProgress.files;
                            const fileLabel = `${uploadProgress.files} Datei${uploadProgress.files > 1 ? 'en' : ''}`;
                            return (
                                <div className={cn("rounded-xl border bg-card p-4", selectedKnowledgeBase ? "mt-5" : "mx-4 mt-4")}>
                                    <div className="flex items-center gap-3">
                                        <motion.span
                                            key={isDone ? 'done' : 'uploading'}
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ duration: 0.2, ease: EASE_OUT }}
                                            className={cn(
                                                "grid size-9 shrink-0 place-items-center rounded-lg",
                                                isDone
                                                    ? "bg-emerald-500/10 text-emerald-600"
                                                    : "bg-primary/10 text-primary",
                                            )}
                                        >
                                            {isDone ? (
                                                <Check className="size-4" aria-hidden />
                                            ) : (
                                                <UploadCloud className="size-4" aria-hidden />
                                            )}
                                        </motion.span>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium text-foreground">
                                                {isDone
                                                    ? `Upload abgeschlossen: ${fileLabel} hochgeladen`
                                                    : `Lade ${fileLabel} hoch...`}
                                            </div>
                                        </div>
                                        {!isDone && (
                                            <Loader2 className="size-4 shrink-0 animate-spin text-primary" aria-hidden />
                                        )}
                                    </div>
                                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
                                        {isDone ? (
                                            <motion.div
                                                className="h-full origin-left rounded-full bg-emerald-500"
                                                initial={{ scaleX: 0 }}
                                                animate={{ scaleX: 1 }}
                                                transition={{ duration: 0.4, ease: EASE_OUT }}
                                            />
                                        ) : (
                                            <motion.div
                                                className="h-full w-1/3 rounded-full bg-primary"
                                                animate={{ x: ["-100%", "300%"] }}
                                                transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* Im Settings-Panel (kein Wissensangebot) füllt der Manager die Fläche
                randlos; eingebettet im Wissensangebot bleibt er eine Karte. */}
            <div
                className={cn(
                    "flex min-h-0 flex-1 flex-col overflow-hidden bg-background",
                    // Eingebettet braucht die Panel-Gruppe eine feste Höhe.
                    selectedKnowledgeBase && "mt-5 h-[70vh] rounded-xl border shadow-sm",
                )}
            >
                <div className="flex min-h-0 flex-1">
                <div className="h-full min-w-0 flex-1 overflow-y-auto p-4">
                    <Tree
                        data={data}
                        isLoading={isInitialLoading}
                        selectedPath={selectedFile?.path}
                        onUpdate={handleUpdate}
                        onNodeClick={handleClick}
                        onFileDrop={handleRootFileDrop}
                        onFileMove={handleRootFileMove}
                        onCreateRootFolder={handleRootFolderCreate}
                        deleteNonEmptyFolders
                        onRefresh={async () => {
                            await updateStorage(true);
                            toast.success('Dateiliste aktualisiert');
                        }}
                    />
                </div>
                {/* Die Details gibt es nur zu einer gewählten Datei: Sie gleiten beim Auswählen
                    ein und beim Abwählen wieder aus. Der Inhalt behält seine feste Breite und
                    wird nur vom wachsenden Rahmen aufgedeckt, damit der Text nicht umbricht. */}
                <AnimatePresence initial={false}>
                {selectedFile && (
                <motion.aside
                    key="details"
                    aria-label="Details"
                    initial={reduceMotion ? false : { width: 0, opacity: 0 }}
                    animate={{ width: DETAILS_WIDTH, opacity: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { width: 0, opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0.1 : 0.3, ease: EASE_OUT }}
                    className="h-full shrink-0 overflow-hidden border-l bg-muted/30"
                >
                <div className="h-full overflow-y-auto p-5" style={{ width: DETAILS_WIDTH }}>
                    <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Details</div>
                        <button
                            type="button"
                            aria-label="Details schließen"
                            onClick={() => setSelectedFile(undefined)}
                            className="grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            <X className="size-4" aria-hidden />
                        </button>
                    </div>
                        {/* Kein Einblenden beim Dateiwechsel: Der Bereich steht schon, nur Neues darin
                            (Vorschau, „Verwendet in“) blendet sich selbst ein. */}
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-muted [&>svg]:size-5">
                                    {fileIconForName(selectedFile.name || selectedFile.path)}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-semibold">{selectedFile.path.split('/').pop()}</div>
                                    {(() => {
                                        const ext = (selectedFile.path.split('.').pop() || '').toUpperCase();
                                        return ext && ext !== selectedFile.path.toUpperCase() ? (
                                            <span className="mt-0.5 inline-flex rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                                {ext}
                                            </span>
                                        ) : null;
                                    })()}
                                </div>
                            </div>
                            <Reveal show={canViewFile(selectedFile)}>
                                <div className="mt-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="min-w-[120px] gap-2"
                                        onClick={() => viewFile(selectedFile)}
                                        data-testid="file-management-view"
                                    >
                                        <Eye className="h-4 w-4" aria-hidden />
                                        {t('documentViewer.view')}
                                    </Button>
                                </div>
                            </Reveal>
                            <Reveal show={selectedFile.path.endsWith('.md') && !selectedKnowledgeBase}>
                                <div className="mt-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="min-w-[120px] gap-2"
                                        onClick={handleOpenMarkdownEditor}
                                    >
                                        <Pencil className="h-4 w-4" aria-hidden />
                                        {t('ui.editMarkdown')}
                                    </Button>
                                </div>
                            </Reveal>
                            {selectedKnowledgeBase && (
                                <>
                                    <div className="mt-3">
                                        <div className="inline-flex items-center gap-2">
                                            <Checkbox id={sharedFieldId} checked={!!selectedFile.shared} onCheckedChange={async (checked) => {
                                                const value = checked === true;
                                                const newSelectedFile = { ...selectedFile };

                                                newSelectedFile.shared = value;

                                                if (value) {
                                                    newSelectedFile.link = '';
                                                }

                                                setSelectedFile(newSelectedFile);

                                                await storageAPI.updateMetadata(authentication.customer, STORAGE_VOLUME, newSelectedFile.path, {
                                                    shared: newSelectedFile.shared,
                                                });
                                                updateStorage();
                                            }} />
                                            <Label htmlFor={sharedFieldId} className="cursor-pointer">Shared</Label>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex flex-col gap-1.5">
                                        <Label htmlFor={linkFieldId}>Link</Label>
                                        <Input
                                            id={linkFieldId}
                                            disabled={selectedFile.shared}
                                            value={selectedFile.link || ''}
                                            onChange={(e) => {
                                                const newSelectedFile = { ...selectedFile };
                                                newSelectedFile.link = e.target.value || '';
                                                setSelectedFile(newSelectedFile);
                                            }}
                                            onBlur={async () => {
                                                await storageAPI.updateMetadata(authentication.customer, STORAGE_VOLUME, selectedFile.path, {
                                                    link: selectedFile.link
                                                });
                                                updateStorage();
                                            }}
                                        />
                                    </div>
                                    <div className="mt-3 flex flex-col gap-1.5">
                                        <Label htmlFor={sourceFieldId}>Quellenangabe</Label>
                                        <Textarea
                                            id={sourceFieldId}
                                            rows={5}
                                            className="resize-none"
                                            value={selectedFile.source || ''}
                                            onChange={(e) => {
                                                const newSelectedFile = { ...selectedFile };
                                                newSelectedFile.source = e.target.value || '';
                                                setSelectedFile(newSelectedFile);
                                            }}
                                            onBlur={async () => {
                                                await storageAPI.updateMetadata(authentication.customer, STORAGE_VOLUME, selectedFile.path, {
                                                    link: selectedFile.link
                                                });
                                                updateStorage();
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="mt-4 flex flex-col gap-2 rounded-lg bg-muted/50 p-3">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <HardDrive className="size-3.5 shrink-0" aria-hidden />
                                    {t('ui.fileSize')}
                                    <span className="ml-auto text-foreground">
                                        {formatFileSize(selectedFile.size) ?? 'N/A'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="size-3.5 shrink-0" aria-hidden />
                                    {t('ui.lastModified')}
                                    <span className="ml-auto text-foreground">
                                        {selectedFile.lastModified ? new Date(selectedFile.lastModified).toLocaleString() : 'N/A'}
                                    </span>
                                </div>
                            </div>
                            <Reveal show={selectedFileKnowledgeBases.length > 0}>
                                <div className="mt-4">
                                    <div className="mb-2 text-sm font-semibold">{t('ui.usedIn')}:</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        <AnimatePresence initial={false}>
                                        {selectedFileKnowledgeBases.map((knowledgeBase: any, index: number) => (
                                            <motion.button
                                                key={knowledgeBase._id}
                                                type="button"
                                                initial={{ opacity: 0, y: 4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 4 }}
                                                transition={{ duration: 0.2, ease: EASE_OUT, delay: index * 0.04 }}
                                                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                                onClick={() => goToKnowledgeBase(knowledgeBase._id, knowledgeBase.name)}
                                            >
                                                <FileText className="size-3 shrink-0" aria-hidden />
                                                <span className="max-w-[200px] truncate">{knowledgeBase.name}</span>
                                            </motion.button>
                                        ))}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </Reveal>
                            <Reveal show={isPdfSelected}>
                                <div className="mt-4">
                                    <div className="mb-2 text-sm font-semibold">Vorschau</div>
                                    {isPdfLoading ? (
                                            <motion.div
                                                key="pdf-skeleton"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.2, ease: EASE_OUT }}
                                                className="flex h-[480px] flex-col gap-3 overflow-hidden rounded-lg border bg-background p-4"
                                                aria-hidden
                                            >
                                                <Skeleton className="h-4 w-3/5" />
                                                <Skeleton className="h-3 w-full" />
                                                <Skeleton className="h-3 w-11/12" />
                                                <Skeleton className="h-3 w-full" />
                                                <Skeleton className="h-3 w-4/5" />
                                                <Skeleton className="mt-2 h-44 w-full rounded-md" />
                                                <Skeleton className="h-3 w-full" />
                                                <Skeleton className="h-3 w-10/12" />
                                                <Skeleton className="h-3 w-full" />
                                                <Skeleton className="h-3 w-2/3" />
                                            </motion.div>
                                        ) : pdfFailed ? (
                                            <motion.div
                                                key="pdf-failed"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.2, ease: EASE_OUT }}
                                                className="flex h-40 flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-center"
                                            >
                                                <div className="text-sm text-muted-foreground">
                                                    Vorschau konnte nicht geladen werden.
                                                </div>
                                            </motion.div>
                                        ) : pdfUrl ? (
                                            <motion.div
                                                key="pdf-viewer"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.25, ease: EASE_OUT }}
                                                className="overflow-hidden rounded-lg border"
                                            >
                                                <iframe
                                                    src={pdfUrl}
                                                    title={`PDF-Vorschau: ${selectedFile.name || selectedFile.path}`}
                                                    className="h-[480px] w-full bg-muted/30"
                                                />
                                            </motion.div>
                                        ) : null}
                                </div>
                            </Reveal>
                        </div>
                </div>
                </motion.aside>
                )}
                </AnimatePresence>
                </div>
            </div>
            
            {/* Markdown Editor Modal */}
            <Dialog
                open={isMarkdownEditorOpen}
                onOpenChange={(open) => {
                    if (!open) handleCancelMarkdown();
                }}
            >
                <DialogContent className="w-[min(1200px,95vw)] max-w-none">
                    <DialogHeader>
                        <DialogTitle>{`Edit: ${selectedFile?.name || 'Markdown File'}`}</DialogTitle>
                    </DialogHeader>
                    <div className="py-2">
                        {isLoadingContent ? (
                            <div className="flex h-[600px] flex-col items-center justify-center gap-4">
                                <Loader2 className="h-7 w-7 animate-spin text-primary" aria-hidden />
                                <div className="text-sm">Loading file content...</div>
                            </div>
                        ) : (
                            <MarkdownEditor
                                value={markdownContent}
                                onChange={handleMarkdownContentChange}
                                height="600px"
                                width="100%"
                            />
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={handleSaveMarkdown}
                            disabled={isLoadingContent || isSavingContent}
                        >
                            {isSavingContent ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancelMarkdown}
                            disabled={isSavingContent}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
